import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { PriorityChip } from '@/components/tasks/PriorityChip';
import { audienceLabelKey } from '@/features/tasks/taskAudience';
import type { TaskListItem } from '@/hooks/useTasks';
import { formatTenantInstant } from './supervisorTaskCalendar';

interface SupervisorTaskDialogProps {
  task: TaskListItem | null;
  timeZone: string;
  canComplete: boolean;
  completing: boolean;
  onComplete: (task: TaskListItem) => void;
  onClose: () => void;
}

/** Summary of a calendar task with the two actions the calendar offers: open it, complete it. */
export function SupervisorTaskDialog({
  task,
  timeZone,
  canComplete,
  completing,
  onComplete,
  onClose,
}: SupervisorTaskDialogProps) {
  const { t, i18n } = useTranslation('calendar');
  const { t: tw } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const locale = i18n.language;

  return (
    <ResponsiveDialog
      open={task !== null}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      mobileTitle={task?.title ?? ''}
    >
      {task && (
        <>
          <DialogTitle>{task.title}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={t(`supervisorTasks.status.${task.status}`)} />
                <PriorityChip priority={task.priority} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={tw(audienceLabelKey(task.audience ?? 'anyone'))}
                />
              </Stack>
              {task.description && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {task.description}
                </Typography>
              )}
              {task.scheduledAt && (
                <Typography variant="body2">
                  {t('supervisorTasks.scheduledAt', {
                    when: formatTenantInstant(task.scheduledAt, locale, timeZone),
                  })}
                </Typography>
              )}
              {task.deadline && (
                <Typography variant="body2">
                  {t('supervisorTasks.deadlineAt', {
                    when: formatTenantInstant(task.deadline, locale, timeZone),
                  })}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {task.assignedTo
                  ? t('supervisorTasks.assignedTo', {
                      name: `${task.assignedTo.firstName} ${task.assignedTo.lastName}`.trim(),
                    })
                  : t('supervisorTasks.unassigned')}
              </Typography>
              {task.booking && (
                <Link
                  component="button"
                  variant="body2"
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() => {
                    const bookingId = task.booking?.id;
                    if (bookingId) void navigate(`/workorders/${bookingId}`);
                  }}
                >
                  {t('supervisorTasks.openWorkOrder', { reg: task.booking.vehicleRegNumber })}
                </Link>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{tc('close')}</Button>
            <Button onClick={() => { void navigate(`/tasks/${task.id}`); }}>
              {t('supervisorTasks.openTask')}
            </Button>
            {canComplete && (
              <Button
                variant="contained"
                disabled={completing}
                onClick={() => { onComplete(task); }}
              >
                {t('supervisorTasks.complete')}
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </ResponsiveDialog>
  );
}
